/**
 *  Menu
 *    Scripts to control menu functionality on the site, mainly used for mobile.
 *
 *  Version:
 *    1.0.0 - 2022/07/08
 */

import { responsiveMediaAbove } from "../../settings/responsive";
import { CLASS_MENU_HOVER, CLASS_SEARCH_OPEN, CLASS_IS_INVERTED, CLASS_MENU_OPEN, CLASS_STICKY, CLASS_VISIBLE, CLASS_IS_MENU_ITEM_HOVER, CLASS_ALLOW_SCROLL } from "../../tools/constants/classes";
import { documentDisableScroll, documentEnableScroll } from "../../tools/document";
// import { setSession } from "../../objects/theme-layout";
import Swiper from 'swiper';

export const SELECTOR_MENU = '[data-main-menu]';
const SELECTOR_SUBMENU = '[data-menu-submenu]';
const SELECTOR_SUBMENU_2 = '[data-menu-submenu="2"]';
const SELECTOR_MENU_INNER = '[data-main-menu-inner]';
const SELECTOR_MENU_TOGGLE = '[data-menu-toggle]';
const SELECTOR_MENU_CLOSE = '[data-menu-close]';
const SELECTOR_SUBMENU_TOGGLE = '[data-submenu-toggle]';
const SELECTOR_CATEGORY_TOGGLE = '[data-category-toggle]';
const SELECTOR_MENU_BACK = '[data-submenu-back]';
const SELECTOR_SUBMENU_LIST_ITEM = '[data-submenu-list-item]';
const SELECTOR_TOPBAR = '[data-topbar]';
const SELECTOR_LINKS = '[data-item-link]';
const SELECTOR_MENU_SLIDER = '[data-menu-slider]'

const ATTR_SUBMENU = 'data-menu-submenu';
const ATTR_HEADER = 'data-header-inverted';

const CLASS_SUBMENU_OPEN = 'is-submenu-open';
const CLASS_MENU_LEVEL = 'is-menu-level-';
const CLASS_MOBILE_MENU_OPEN = 'is-mobile-open';

export class Menu {
  container:HTMLElement;
  header:HTMLElement;
  submenus:NodeListOf<HTMLUListElement> | null = null;
  menuLevel:number = 1;
  currentMenu:HTMLElement | null = null;
  topBar:HTMLElement | null = null;
  headerLinks:NodeListOf<HTMLElement> | null = null;
  mainMenuInner: NodeListOf<HTMLElement> | null = null;
  menuSliders: NodeListOf<HTMLElement> | null = null;
  swipers: Swiper[] = [];

  constructor(container:HTMLElement, header:HTMLElement) {
    this.container = container;
    this.header = header;
    this.topBar = document.querySelector(SELECTOR_TOPBAR);
    this.submenus = container.querySelectorAll(SELECTOR_SUBMENU);
    this.headerLinks = this.header.querySelectorAll(SELECTOR_LINKS);
    this.mainMenuInner = container.querySelectorAll(SELECTOR_MENU_INNER)
    this.menuSliders = container.querySelectorAll(SELECTOR_MENU_SLIDER);

    this.attachEvents();
    this.initialiseMenuSlider();
    
    window.addEventListener("resize", this.handleScreenResize);
    document.addEventListener('click', this.handleClickOutside);
  }

  attachEvents() {
    document.querySelectorAll<HTMLButtonElement>(SELECTOR_MENU_TOGGLE)
    .forEach(element => {
      element.addEventListener('click', this.onMenuToggle);
    });

    document.querySelectorAll<HTMLButtonElement>(SELECTOR_MENU_CLOSE)
    .forEach(element => {
      element.addEventListener('click', this.closeMenu);
    });

    this.container.querySelectorAll<HTMLAnchorElement>(SELECTOR_SUBMENU_TOGGLE)
    .forEach(element => {
      element.addEventListener('click', (e)=> {
       e.preventDefault();
        if (element.classList.contains(CLASS_MENU_OPEN)) {
          element.classList.remove(CLASS_MENU_OPEN, CLASS_MOBILE_MENU_OPEN);
          this.onSubmenuClose(e);
        }
        else {
          this.onSubmenuOpen(e);
        }
        if(!responsiveMediaAbove('medium') && element.classList.contains(CLASS_MOBILE_MENU_OPEN)) {
          element.classList.remove(CLASS_MENU_OPEN, CLASS_MOBILE_MENU_OPEN);
          this.onSubmenuClose(e);
        }
      });

      const elementContainer = element.closest(SELECTOR_SUBMENU_LIST_ITEM);

      if (elementContainer) {
        elementContainer.addEventListener('mouseover', (e) => {
          document.body.classList.add(CLASS_IS_MENU_ITEM_HOVER);
        })
  
        elementContainer.addEventListener('mouseleave', (e) => {
          document.body.classList.remove(CLASS_IS_MENU_ITEM_HOVER);
        })
      }

      this.mainMenuInner?.forEach(inner => {
        inner.addEventListener('mouseleave', (e) => {
          document.body.classList.remove(CLASS_IS_MENU_ITEM_HOVER);
        })
      })
    });

    // Forcing all selector links triggers adding class
    const mainLinks = this.container.querySelectorAll(SELECTOR_LINKS);

    if (mainLinks.length > 0) {
      mainLinks.forEach(link => {
        link.addEventListener('mouseover', (e) => {
          document.body.classList.add(CLASS_IS_MENU_ITEM_HOVER);
        })
      })
    }

    this.container.querySelectorAll<HTMLAnchorElement>(SELECTOR_CATEGORY_TOGGLE)
    .forEach(element => {
      element.addEventListener('click', (e)=> {
        this.onCategoryMenuOpen(e)
      })
    })
    
    // Hover menu on Desktop
    this.header.addEventListener('mouseover', ()=> {
      if(responsiveMediaAbove('medium')) {
        this.header.classList.add(CLASS_MENU_HOVER);
      }
    });

    this.header.addEventListener('mouseout', ()=> {
      if(responsiveMediaAbove('medium')) {
        this.header.classList.remove(CLASS_MENU_HOVER);
      }
    });

    this.container.querySelectorAll<HTMLAnchorElement>(SELECTOR_SUBMENU_2).forEach(item => {
      item.addEventListener('mouseover', ()=> {
        if(responsiveMediaAbove('medium')) {
          this.header.classList.add(CLASS_MENU_HOVER,CLASS_MENU_HOVER);
          item.parentElement?.classList.add(CLASS_MENU_OPEN);
        }
      });
      item.addEventListener('mouseout', ()=> {
        if(responsiveMediaAbove('medium')) {
          item.parentElement?.classList.remove(CLASS_MENU_OPEN);
          this.header.classList.remove(CLASS_MENU_OPEN);
          this.header.classList.remove(CLASS_MENU_HOVER);
        }
      });
    })

    this.container.querySelectorAll<HTMLAnchorElement>(SELECTOR_MENU_BACK)
    .forEach(element => {
      element.addEventListener('click', this.onSubmenuBack);
    });

    this.initialiseMenuSlider()
  }

  /*
    Events
  */
  handleScreenResize = () => {
    this.initialiseMenuSlider();
    this.resetSubmenuStyles();
    if(responsiveMediaAbove('medium')) {
      documentEnableScroll();
      this.container.classList.remove(CLASS_MENU_OPEN);
      this.header.classList.remove(CLASS_MENU_OPEN, CLASS_SEARCH_OPEN);
      this.closeMenu();
    }
  };

  onMenuToggle = (e:Event) => {
    e.preventDefault();
    if(responsiveMediaAbove('medium')) {
      this.container.classList.remove(CLASS_MENU_OPEN);
      this.header.classList.remove(CLASS_MENU_HOVER, CLASS_MENU_OPEN, CLASS_SEARCH_OPEN);
    } 
    else {
      if(this.header.classList.contains(CLASS_MENU_OPEN)) {
        this.container.classList.remove(CLASS_MENU_OPEN);
        this.closeMenu();
      }
      else {
        // if(this.header.hasAttribute(ATTR_HEADER)) this.header.style.top = '0';
        this.openMenu();
      }
    }
  }

  handleClickOutside = (event: MouseEvent) => {
    const target = event.target as Node;
  
    const isToggle = (event.target as HTMLElement).closest(SELECTOR_MENU_TOGGLE);
    if (isToggle) return;
  
    const menuInnerEls = document.querySelectorAll<HTMLElement>(SELECTOR_MENU_INNER);
  
    const clickedInside = Array.from(menuInnerEls).some(inner =>
      inner.contains(target)
    );
  
    if (!clickedInside && this.header.classList.contains(CLASS_MENU_OPEN)) {
      this.closeMenu();
    }
  };

  onSubmenuClose = (e:Event) => {
    const thisTarget = e.currentTarget as HTMLElement;
    const newSubmenu = thisTarget?.parentElement?.querySelector<HTMLUListElement>(SELECTOR_SUBMENU);

    newSubmenu?.classList.remove(CLASS_SUBMENU_OPEN);

    if(responsiveMediaAbove('medium') && this.header) {
      this.container.classList.remove(CLASS_MENU_OPEN);
      this.header.classList.remove(CLASS_MENU_OPEN);
    }

    this.container.querySelectorAll<HTMLAnchorElement>(SELECTOR_SUBMENU_TOGGLE).forEach(element => {
      element.classList.remove(CLASS_MENU_OPEN);
    });

    if(!responsiveMediaAbove('medium')) {
      documentEnableScroll();
    }
  }

  onSubmenuOpen = (e:Event) => {
    e.preventDefault();
    //Finding the child submenu of the link clicked on
    const thisTarget = e.currentTarget as HTMLElement;
    const newSubmenu = thisTarget?.parentElement?.querySelector<HTMLUListElement>(SELECTOR_SUBMENU);

    thisTarget.classList.add(CLASS_MENU_OPEN);
    if(!newSubmenu || !newSubmenu.getAttribute(ATTR_SUBMENU)) {
      return console.warn('Error: Menu structure has changed.');
    }
    
    this.updateSubmenuClasses(null, newSubmenu);

    if(!responsiveMediaAbove('medium')) {
      documentDisableScroll();
    }
  }

  onCategoryMenuOpen = (e: Event) => {
    e.preventDefault();
    const thisTarget = e.currentTarget as HTMLElement;
    const parentUL = thisTarget.closest('ul'); // Find the closest parent UL
    const newSubmenu = thisTarget?.parentElement?.querySelector<HTMLUListElement>(SELECTOR_SUBMENU);
  
    if (!newSubmenu || !parentUL) {
      return console.warn("Error: Menu structure has changed.");
    }
    
    const isAlreadyOpen = newSubmenu.classList.contains(CLASS_SUBMENU_OPEN);
  
    if (isAlreadyOpen) {
      newSubmenu.classList.remove(CLASS_SUBMENU_OPEN);
    } else {
      parentUL.querySelectorAll<HTMLUListElement>(`${SELECTOR_SUBMENU}[data-menu-submenu="3"]`).forEach(submenu => {
        if (submenu !== newSubmenu) {
          submenu.classList.remove(CLASS_SUBMENU_OPEN);
        }
      });

      newSubmenu.classList.add(CLASS_SUBMENU_OPEN);
    }
  };

  onSubmenuBack = (e:Event) => {
    e.preventDefault();

    //Element you've clicked on
    const parentElement = this.currentMenu?.closest('li');
    const newSubmenu = parentElement?.closest<HTMLElement>(SELECTOR_SUBMENU);

    if(!newSubmenu) return console.warn('Error: Menu structure has changed.');

    // close all submenus
    this.headerLinks?.forEach(e => e.classList.remove(CLASS_MENU_OPEN, CLASS_MOBILE_MENU_OPEN))

    this.updateSubmenuClasses(this.currentMenu!, newSubmenu!);
  }

  /*
    Methods
  */

  //Closing all submenus and hiding the drawer
  closeMenu = () => {
    let activeMenu = document.querySelector<HTMLElement>(SELECTOR_MENU);
    this.topBar?.classList.remove(CLASS_MENU_OPEN);

    activeMenu?.classList.remove(CLASS_MENU_OPEN);
    activeMenu?.removeAttribute('data-lenis-prevent'); // remove attribute upon closing the menu
    const scrollY = document.body.getAttribute('data-scroll-y');
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    document.body.style.overflow = '';
    document.body.removeAttribute('data-scroll-y');

    if (scrollY) {
      window.scrollTo(0, parseInt(scrollY));
    }
    
    // Remove data-lenis-prevent from the next sibling data-menu-toggle element
    const nextSiblingToggle = activeMenu?.nextElementSibling as HTMLElement;
    if (nextSiblingToggle && nextSiblingToggle.matches(SELECTOR_MENU_TOGGLE)) {
      nextSiblingToggle.removeAttribute('data-lenis-prevent'); // remove attribute upon closing the menu
    }
    
    this.header.classList.remove(CLASS_MENU_OPEN);
    if(!this.header.hasAttribute(ATTR_HEADER)) this.header.classList.remove(CLASS_IS_INVERTED);

    activeMenu?.querySelector<HTMLElement>(SELECTOR_SUBMENU)?.classList.add(CLASS_SUBMENU_OPEN);
    this.resetSubmenuClasses();
    this.updateMenuStateClasses(1);
    documentEnableScroll();
    activeMenu?.querySelectorAll<HTMLAnchorElement>(SELECTOR_SUBMENU_TOGGLE).forEach(element => {
      if (element.classList.contains(CLASS_MENU_OPEN)) {
        element.classList.remove(CLASS_MENU_OPEN);
      }
    });

    document.querySelector('body')?.classList.remove(CLASS_MENU_OPEN)
  }

  openMenu = () => {
    const activeMenu = document.querySelector<HTMLElement>(SELECTOR_MENU);
    
    if (!activeMenu) {
      console.warn("No active menu found.");
      return;
    }
  
    activeMenu.classList.add(CLASS_MENU_OPEN);
    activeMenu.setAttribute('data-lenis-prevent', ''); // this will prevent the page from scrolling when the menu is open
    // override lenis scroll
    const scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';

    document.body.setAttribute('data-scroll-y', scrollY.toString());
    
    // Add data-lenis-prevent to the next sibling data-menu-toggle element
    const nextSiblingToggle = activeMenu.nextElementSibling as HTMLElement;
    if (nextSiblingToggle && nextSiblingToggle.matches(SELECTOR_MENU_TOGGLE)) {
      nextSiblingToggle.setAttribute('data-lenis-prevent', ''); // this will prevent the page from scrolling when the menu is open
    }
    
    this.header.classList.add(CLASS_MENU_OPEN, CLASS_IS_INVERTED);
    this.topBar?.classList.add(CLASS_MENU_OPEN);
  
    const topBarHeight = this.header.classList.contains(CLASS_STICKY) ? 0 : this.topBar?.clientHeight || 0;
    const topOffset = topBarHeight;
  
    // Adjust submenu positioning
    document.querySelectorAll<HTMLElement>(SELECTOR_SUBMENU_2).forEach((element) => {
      element.style.top = `-${topOffset + this.header.clientHeight}px`;
      element.style.paddingTop = `${topOffset}px`;
      element.style.paddingBottom = `${topOffset}px`;
    });
  
    // Ensure mainMenuInner exists before modifying styles
    activeMenu.style.top = `${topOffset}px`;

    document.querySelector('body')?.classList.add(CLASS_MENU_OPEN)
  
    documentDisableScroll();
  };

  resetSubmenuClasses = () => {
    this.container.querySelectorAll<HTMLElement>(SELECTOR_SUBMENU).forEach(submenu => {
      submenu.classList.remove(CLASS_SUBMENU_OPEN);
      submenu.classList.remove(CLASS_MENU_OPEN);
    });
  }

  updateSubmenuClasses = (thisSubmenu:HTMLElement | null, newSubmenu:HTMLElement) => {
    const newLevel = parseInt(newSubmenu.getAttribute(ATTR_SUBMENU) || '');
    if(!newLevel || !this.submenus) return;
    thisSubmenu?.classList.remove(CLASS_SUBMENU_OPEN);

    Array.from(this.submenus).forEach(menu => {
      const equalNode = menu.isEqualNode(newSubmenu);
      const innerMenuLevel = parseInt(menu.getAttribute(ATTR_SUBMENU) || '');
      //Close other submenus and open active one on level 3
      if(equalNode) {
        menu.classList.add(CLASS_SUBMENU_OPEN);
        // Apply manual scroll handling for level 2 submenus
        if (newLevel === 2) {
          this.allowScrollingOnSubmenu(menu);
        }
      }
    });

    this.currentMenu = newSubmenu;

    this.updateMenuStateClasses(newLevel);
  }

  updateMenuStateClasses = (newLevel:number)  => { 
    this.container.classList.remove(`${CLASS_MENU_LEVEL}${this.menuLevel}`);
    this.container.classList.add(`${CLASS_MENU_LEVEL}${newLevel}`);
    this.menuLevel = newLevel;

    if(responsiveMediaAbove('medium') && this.header) {
      if(!this.header.classList.contains(CLASS_MENU_OPEN) && newLevel > 1) {
        this.header.classList.add(CLASS_MENU_OPEN);
      } else if(newLevel == 1) {
        this.header.classList.remove(CLASS_MENU_OPEN);
      }
    }
  }

  // Menu Slider
  initialiseMenuSlider = () => {
    if (this.menuSliders && this.menuSliders.length > 0) {
      this.menuSliders.forEach((slide, index) => {
        // If viewport is >= 1100px, destroy the Swiper instance
        if (window.innerWidth >= 1100) {
          if (this.swipers[index]) {
            this.swipers[index].destroy(true, true);
            this.swipers[index] = null;
          }

          this.calculateImageCols(slide)
          return;
        }
  
        // If Swiper doesn't exist, initialize it
        if (!this.swipers[index]) {
          this.swipers[index] = new Swiper(slide, {
            slidesPerView: 2,
            spaceBetween: 12,
            loop: false
          });
        }
      });
    }
  };

  calculateImageCols = (slide) => {
    const imagesGridContainer = slide.closest('[data-images-container]');
    const images = slide.querySelectorAll('[data-slide-item]');
  
    if (!imagesGridContainer || images.length === 0) return;
  
    const classList = Array.from(images)
      .map(img => img.getAttribute('data-slide-item'))
      .join('-');
  
    imagesGridContainer.classList.add(`grid-${classList}`);
    
    imagesGridContainer.setAttribute("data-image-count", images.length);
  };

  resetSubmenuStyles = () => {
    if(responsiveMediaAbove('medium')) {
      this.container.style.top = '';
      document.querySelectorAll<HTMLElement>(SELECTOR_SUBMENU_2).forEach((element) => {
        element.style.top = '';
        element.style.paddingTop = '';
        element.style.paddingBottom = '';
      });
    }
  }

  allowScrollingOnSubmenu = (submenu: HTMLElement) => {
    // Remove lenis prevent attributes
    submenu.removeAttribute('data-lenis-prevent');
    submenu.removeAttribute('data-lenis-prevent-wheel');
    submenu.removeAttribute('data-lenis-prevent-touch');
    
    // Add class to allow scrolling (CSS fallback)
    submenu.classList.add(CLASS_ALLOW_SCROLL);
    
    // Add manual scroll handling to bypass Lenis
    submenu.addEventListener('wheel', this.handleManualScroll as EventListener, { passive: false });
    submenu.addEventListener('touchstart', this.handleTouchStart as EventListener, { passive: false });
    submenu.addEventListener('touchmove', this.handleTouchMove as EventListener, { passive: false });
  }

  private handleManualScroll = (e: Event): void => {
    const wheelEvent = e as WheelEvent;
    const target = wheelEvent.currentTarget as HTMLElement;
    if (target) {
      wheelEvent.preventDefault();
      wheelEvent.stopPropagation();
      target.scrollTop += wheelEvent.deltaY;
    }
  }

  private touchStartY = 0;
  private handleTouchStart = (e: Event): void => {
    const touchEvent = e as TouchEvent;
    this.touchStartY = touchEvent.touches[0].clientY;
  }

  private handleTouchMove = (e: Event): void => {
    const touchEvent = e as TouchEvent;
    const target = touchEvent.currentTarget as HTMLElement;
    if (target) {
      touchEvent.preventDefault();
      touchEvent.stopPropagation();
      
      const touchY = touchEvent.touches[0].clientY;
      const deltaY = this.touchStartY - touchY;
      
      target.scrollTop += deltaY;
      this.touchStartY = touchY;
    }
  }

}